import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Icon from 'application/components/icon';
import useTranslate from 'application/hooks/use-translate';
import LoadingState from 'application/pages/groups/group/components/reports/loading-state';
import NoDataAvailableState from 'application/pages/groups/group/components/reports/no-data-available-state';
import ReportBillingPeriodSelector from 'application/pages/groups/group/components/billing-period-selector';
import { Period } from 'application/pages/groups/group/components/reports/types';
import ReportService from 'application/services/report-service';
import HTMLReport from 'application/pages/groups/group/components/reports/html-report';
import DownloadButton from 'application/components/download-button';
import useToast from 'application/hooks/use-toast';
import downloadFile from 'application/utilities/download-file';

interface Props {
  groupId: string;
}

const PaymentRequestsByCategoryReport = ({ groupId }: Props) => {
  const translate = useTranslate('reports');
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<Period | null>(null);
  const { data, isFetching, isSuccess } = useQuery({
    queryKey: ['group-statement-report', selectedBillingPeriod],
    queryFn: () => {
      if (!selectedBillingPeriod) {
        return Promise.reject();
      }

      const { start, end } = selectedBillingPeriod as Period;

      return ReportService.fetchPoolPaymentRequestsByCategory(groupId, {
        periodStart: start as number,
        periodEnd: end as number
      });
    },
    enabled: !!selectedBillingPeriod
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const { addToast } = useToast();
  const fetchFile = () => {
    setIsDownloading(true);

    const { start, end } = selectedBillingPeriod as Period;

    return ReportService.downloadPoolPaymentRequestsByCategory(groupId, {
      periodStart: start as number,
      periodEnd: end as number
    })
      .then((response) => {
        if (response.status === 404) {
          addToast({
            message: translate('no-data-available'),
            type: 'warning'
          });

          return;
        }

        if (!response.ok) {
          addToast({
            message: translate('error-downloading-report'),
            type: 'error'
          });

          return;
        }

        downloadFile(response);
      })
      .catch(() => {
        addToast({
          message: translate('error-downloading-report'),
          type: 'error'
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <>
      <div className="absolute right-4 top-4 flex items-center print:hidden">
        <ReportBillingPeriodSelector
          groupId={groupId}
          onChange={setSelectedBillingPeriod}
          value={selectedBillingPeriod}
        />
        <DownloadButton disabled={!data || !isSuccess || isFetching} downloading={isDownloading} onClick={fetchFile} />
      </div>

      {isFetching && <LoadingState />}

      {data && isSuccess && !isFetching && <HTMLReport report={data} />}

      {!isFetching && !data && isSuccess && <NoDataAvailableState />}

      {!isFetching && !data && !isSuccess && (
        <div className="flex h-96 flex-col items-center justify-center gap-4 bg-base-200">
          <Icon icon="calendar-range" className="text-4xl text-gray-400" />

          <span className="w-64 text-center text-gray-400">{translate('select-billing-period')}</span>
        </div>
      )}
    </>
  );
};

export default PaymentRequestsByCategoryReport;
